import { baseUrl } from 'volto-ebc-radiomec/utils/api';

import { getProgramsSchedule } from './programsSchedule';
import { getStations } from './stations';

import type {
  NowPlayingPayload,
  PublicResolvedSchedulePayload,
  PublicScheduleItem,
  ResolvedSchedulePayload,
  ScheduleItemPayload,
} from '../../interfaces/schedule';

type GetScheduleParams = {
  broadcaster: string;
  week_day: number;
};

type GetResolvedScheduleParams = {
  stationId: string;
  date: string;
};

type GetNowPlayingParams = {
  stationId: string;
  date: string;
  time: string;
};

type ScheduleApiResponse = {
  schedule_data?: any[];
  exceptions?: any[];
};

type ScheduleContextPayload = {
  schedule_data: ScheduleItemPayload[];
  exceptions: ScheduleItemPayload[];
};

type ProgramMeta = {
  title: string;
  description: string;
  url?: string;
};

export type RadioScheduleItemData = {
  id: string | number;
  hour: string;
  station: string;
  program: string;
  nextProgramLabel?: string;
  tagLabel?: string;
  stationId?: string;
  isLive?: boolean;
};

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}`;
}

function getEndTime(startTime: string, durationMinutes: number): string {
  return minutesToTime(timeToMinutes(startTime) + durationMinutes);
}

function getWeekdayFromDate(date: string): number {
  return new Date(`${date}T12:00:00`).getDay();
}

function formatDateToISO(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function normalizeItems(items: any[] = []): ScheduleItemPayload[] {
  return items.map((item, index) => ({
    id:
      String(item.id ?? '') ||
      `item-${index}-${item.programId ?? 'program'}-${item.startTime ?? '00:00'}`,
    programId: item.programId ?? null,
    titleOverride: item.titleOverride ?? '',
    descriptionOverride: item.descriptionOverride ?? '',
    linkOverride: item.linkOverride ?? '',
    startTime: item.startTime ?? '00:00',
    durationMinutes: Number(item.durationMinutes ?? 0),
    isLive: !!item.isLive,
    isRerun: !!item.isRerun,
    notes: item.notes ?? '',
    status: item.status ?? 'scheduled',
    linkedStationIds: Array.isArray(item.linkedStationIds)
      ? item.linkedStationIds
      : [],
    isTimeLocked: !!item.isTimeLocked,
    date: item.date ?? undefined,
    reason: item.reason ?? '',
  }));
}

function normalizeScheduleResponse(
  data: ScheduleApiResponse | null | undefined,
): ScheduleContextPayload {
  return {
    schedule_data: normalizeItems(
      Array.isArray(data?.schedule_data) ? data.schedule_data : [],
    ),
    exceptions: normalizeItems(
      Array.isArray(data?.exceptions) ? data.exceptions : [],
    ).map((item) => ({
      ...item,
      status: item.status ?? 'special',
    })),
  };
}

function serializeWeeklyItems(items: ScheduleItemPayload[]) {
  return items.map((item) => ({
    programId: item.programId,
    titleOverride: item.titleOverride ?? '',
    descriptionOverride: item.descriptionOverride ?? '',
    linkOverride: item.linkOverride ?? '',
    startTime: item.startTime,
    durationMinutes: Number(item.durationMinutes ?? 0),
    isLive: !!item.isLive,
    isRerun: !!item.isRerun,
    notes: item.notes ?? '',
    status: item.status ?? 'scheduled',
  }));
}

function serializeExceptionItems(items: ScheduleItemPayload[]) {
  return items.map((item) => ({
    programId: item.programId,
    titleOverride: item.titleOverride ?? '',
    descriptionOverride: item.descriptionOverride ?? '',
    linkOverride: item.linkOverride ?? '',
    startTime: item.startTime,
    durationMinutes: Number(item.durationMinutes ?? 0),
    isLive: !!item.isLive,
    isRerun: !!item.isRerun,
    notes: item.notes ?? '',
    date: item.date,
    reason: item.reason ?? '',
  }));
}

function isItemPlaying(
  item: {
    startTime: string;
    durationMinutes: number;
  },
  currentTime: string,
) {
  const start = timeToMinutes(item.startTime);
  const end = timeToMinutes(getEndTime(item.startTime, item.durationMinutes));
  const current = timeToMinutes(currentTime);

  return current >= start && current < end;
}

function getCurrentTimeString(date = new Date()) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
}

function resolveDisplayTitle(
  item: ScheduleItemPayload,
  programMap: Map<string, ProgramMeta>,
): string {
  const override = item.titleOverride?.trim();

  if (override) return override;

  if (item.programId) {
    const programMeta = programMap.get(item.programId);

    if (programMeta?.title) return programMeta.title;

    return item.programId;
  }

  return 'Programa';
}

function resolveDisplayDescription(
  item: ScheduleItemPayload,
  programMap: Map<string, ProgramMeta>,
): string {
  const override = item.descriptionOverride?.trim();

  if (override) return override;

  if (item.programId) {
    const programMeta = programMap.get(item.programId);
    return programMeta?.description?.trim() || '';
  }

  return '';
}

function mapToPublicItem(
  item: ScheduleItemPayload,
  currentTime: string,
  programMap: Map<string, ProgramMeta>,
): PublicScheduleItem {
  return {
    id: item.id,
    programId: item.programId,

    programUrl:
      item.linkOverride?.trim() ||
      (item.programId ? programMap.get(item.programId)?.url || '' : ''),

    title: resolveDisplayTitle(item, programMap),
    description: resolveDisplayDescription(item, programMap),
    startTime: item.startTime,
    endTime: getEndTime(item.startTime, item.durationMinutes),
    durationMinutes: item.durationMinutes,
    isLive: !!item.isLive,
    isRerun: !!item.isRerun,
    isTimeLocked: !!item.isTimeLocked,
    status: item.status ?? 'scheduled',
    isNowPlaying: isItemPlaying(item, currentTime),
    date: item.date,
    reason: item.reason,
  };
}

function getNextItem<T extends { id: string | number }>(
  items: T[],
  currentItemId: string | number,
): T | null {
  const currentIndex = items.findIndex((item) => item.id === currentItemId);

  if (currentIndex < 0) return null;

  return items[currentIndex + 1] ?? null;
}

function getUpcomingItems(
  items: PublicScheduleItem[],
  currentItem: PublicScheduleItem | null,
  currentTime: string,
  limit = 5,
): PublicScheduleItem[] {
  if (!items.length) return [];

  if (currentItem) {
    const currentIndex = items.findIndex((item) => item.id === currentItem.id);

    if (currentIndex < 0) return [];

    return items.slice(currentIndex + 1, currentIndex + 1 + limit);
  }

  const currentMinutes = timeToMinutes(currentTime);

  return items
    .filter((item) => timeToMinutes(item.startTime) > currentMinutes)
    .slice(0, limit);
}

function sortItemsByStartTime<T extends { startTime: string }>(
  items: T[],
): T[] {
  return [...items].sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
  );
}

function resolveItemsForDate(
  payload: ScheduleContextPayload,
  date: string,
): {
  items: ScheduleItemPayload[];
  source: 'weekly' | 'exception';
} {
  const exceptionItems = payload.exceptions.filter(
    (item) => item.date === date,
  );

  if (exceptionItems.length) {
    return {
      items: sortItemsByStartTime(exceptionItems),
      source: 'exception',
    };
  }

  return {
    items: sortItemsByStartTime(payload.schedule_data),
    source: 'weekly',
  };
}

/**
 * ========= API BASE =========
 */

export async function getScheduleContext({
  broadcaster,
  week_day,
}: GetScheduleParams): Promise<ScheduleContextPayload> {
  const url = `${baseUrl}/@schedule?broadcaster=${encodeURIComponent(
    broadcaster,
  )}&week_day=${week_day}`;

  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 204) {
    return {
      schedule_data: [],
      exceptions: [],
    };
  }

  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `Erro ao buscar grade (${response.status}) - ${text || 'sem resposta'}`,
    );
  }

  const data: ScheduleApiResponse = text ? JSON.parse(text) : {};

  return normalizeScheduleResponse(data);
}

export async function getScheduleByContext({
  broadcaster,
  week_day,
}: GetScheduleParams): Promise<ScheduleItemPayload[]> {
  const payload = await getScheduleContext({
    broadcaster,
    week_day,
  });

  return payload.schedule_data;
}

export async function saveScheduleContext({
  broadcaster,
  week_day,
  scheduleItems,
  exceptionItems,
}: {
  broadcaster: string;
  week_day: number;
  scheduleItems?: ScheduleItemPayload[];
  exceptionItems?: ScheduleItemPayload[];
}) {
  const payload: Record<string, unknown> = {};

  if (scheduleItems) {
    payload.schedule_data = serializeWeeklyItems(scheduleItems);
  }

  if (exceptionItems) {
    payload.exceptions = serializeExceptionItems(exceptionItems);
  }

  const url = `${baseUrl}/@schedule?broadcaster=${encodeURIComponent(
    broadcaster,
  )}&week_day=${week_day}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 204) {
    return {};
  }

  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(
      `Erro ao salvar grade (${response.status}): ${
        responseText || 'sem corpo de resposta'
      }`,
    );
  }

  try {
    return responseText ? JSON.parse(responseText) : {};
  } catch {
    return {};
  }
}

export async function saveScheduleByContext({
  broadcaster,
  week_day,
  items,
}: {
  broadcaster: string;
  week_day: number;
  items: ScheduleItemPayload[];
}) {
  return saveScheduleContext({
    broadcaster,
    week_day,
    scheduleItems: items,
  });
}

/**
 * ========= CAMADA PÚBLICA =========
 */

export async function getPublicResolvedSchedule({
  stationId,
  date,
  time,
}: {
  stationId: string;
  date: string;
  time?: string;
}): Promise<PublicResolvedSchedulePayload> {
  const weekDay = getWeekdayFromDate(date);
  const currentTime = time || getCurrentTimeString();

  const [scheduleContext, programsSchedule] = await Promise.all([
    getScheduleContext({
      broadcaster: stationId,
      week_day: weekDay,
    }),
    getProgramsSchedule(),
  ]);

  const { items: resolvedItems } = resolveItemsForDate(scheduleContext, date);

  const programMap = new Map<string, ProgramMeta>(
    programsSchedule.map((item) => [
      item.token,
      {
        title: item.title,
        description: item.description ?? '',
        url: item['@id'] ?? '',
      },
    ]),
  );

  const items = resolvedItems.map((item) =>
    mapToPublicItem(item, currentTime, programMap),
  );

  const currentItem = items.find((item) => item.isNowPlaying) ?? null;
  const nextItem = currentItem ? getNextItem(items, currentItem.id) : null;
  const upcomingItems = getUpcomingItems(items, currentItem, currentTime, 5);

  return {
    stationId,
    date,
    weekDay,
    label: undefined,
    items,
    currentItem,
    nextItem,
    upcomingItems,
  };
}

/**
 * ========= COMPAT LEGACY =========
 */

export async function getResolvedSchedule({
  stationId,
  date,
}: GetResolvedScheduleParams): Promise<ResolvedSchedulePayload | null> {
  const weekday = getWeekdayFromDate(date);

  const scheduleContext = await getScheduleContext({
    broadcaster: stationId,
    week_day: weekday,
  });

  const { items, source } = resolveItemsForDate(scheduleContext, date);

  return {
    stationId,
    date,
    source,
    sourceId:
      source === 'exception'
        ? `${stationId}-${date}`
        : `${stationId}-${weekday}`,
    items,
  };
}

export async function getNowPlaying({
  stationId,
  date,
  time,
}: GetNowPlayingParams): Promise<NowPlayingPayload> {
  const resolved = await getResolvedSchedule({
    stationId,
    date,
  });

  if (!resolved) {
    return {
      stationId,
      date,
      time,
      currentItem: null,
      nextItem: null,
      source: null,
      sourceId: null,
    };
  }

  const currentItem =
    resolved.items.find((item) => isItemPlaying(item, time)) ?? null;

  const nextItem = currentItem
    ? getNextItem(resolved.items, currentItem.id)
    : null;

  return {
    stationId,
    date,
    time,
    currentItem,
    nextItem,
    source: resolved.source,
    sourceId: resolved.sourceId,
  };
}

/**
 * ========= HOME / SIDEBARS =========
 */

export async function getRadioSchedule(): Promise<RadioScheduleItemData[]> {
  try {
    const stations = await getStations();

    if (!stations.length) {
      return [];
    }

    const date = formatDateToISO();

    const items = await Promise.all(
      stations.map(async (station) => {
        try {
          const resolved = await getPublicResolvedSchedule({
            stationId: station.id,
            date,
          });

          const currentItem =
            resolved.currentItem ?? resolved.items?.[0] ?? null;

          if (!currentItem) {
            return null;
          }

          const nextItem =
            resolved.nextItem ??
            resolved.items.find((item) => item.id !== currentItem.id) ??
            null;

          return {
            id: `${station.id}-${date}`,
            stationId: station.id,
            hour: currentItem.startTime || '--:--',
            station: station.shortName || station.title || 'Rádio',
            program: currentItem.title || 'Programa',
            nextProgramLabel: nextItem?.title
              ? `A seguir | ${nextItem.title}`
              : 'A seguir | Programação da emissora',
            tagLabel: currentItem.isNowPlaying
              ? currentItem.isLive
                ? 'ao vivo'
                : 'agora'
              : 'ouvir',
            isLive: !!currentItem.isLive,
          };
        } catch (error) {
          console.error(
            `[getRadioSchedule] Falha ao montar card da emissora ${station.id}:`,
            error,
          );
          return null;
        }
      }),
    );

    return items.filter(Boolean) as RadioScheduleItemData[];
  } catch (error) {
    console.error('Falha ao montar programação das rádios:', error);
    return [];
  }
}
